/**
 * Состояние системы.
 *
 * Служебный экран для владельца: работает ли связь с API, готовы ли база и
 * объектное хранилище. Здесь же показан реестр типов ProjectItem — единый
 * источник, из которого выводятся формы, фильтры и валидация.
 */

import { DatabaseIcon, HardDriveIcon } from 'lucide-react';
import {
  DISPLAY_TIMEZONE,
  getItemTypesByGroup,
  ITEM_GROUPS,
  ITEM_GROUP_LABELS_RU,
  type HealthCheck,
  type HealthState,
} from '@project-os/contracts';
import { useHealth } from '@/api/health';
import { StatusBadge } from '@/components/common/status-badge';
import { ErrorState } from '@/components/common/states';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

const STATE_LABELS: Record<HealthState, { label: string; tone: 'success' | 'warning' | 'danger' }> =
  {
    ok: { label: 'Работает', tone: 'success' },
    degraded: { label: 'Частично доступен', tone: 'warning' },
    down: { label: 'Недоступен', tone: 'danger' },
  };

function CheckRow({
  icon,
  name,
  check,
}: {
  icon: React.ReactNode;
  name: string;
  check: HealthCheck;
}) {
  const state = STATE_LABELS[check.state];
  return (
    <div className="flex items-center justify-between gap-4 border-b py-2.5 last:border-b-0">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="text-muted-foreground [&>svg]:size-4">{icon}</span>
        <div className="min-w-0">
          <div className="text-sm">{name}</div>
          {check.message ? (
            <div className="mt-0.5 truncate text-xs text-muted-foreground">{check.message}</div>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="text-xs text-muted-foreground" data-numeric>
          {check.durationMs} мс
        </span>
        <StatusBadge label={state.label} tone={state.tone} />
      </div>
    </div>
  );
}

function HealthCard() {
  const { data, isPending, isError, error, refetch } = useHealth();

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0 pb-3">
        <div>
          <CardTitle className="text-sm">Проверка зависимостей</CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Обновляется автоматически каждые 15 секунд
          </p>
        </div>
        {data ? <StatusBadge {...STATE_LABELS[data.state]} /> : null}
      </CardHeader>
      <CardContent>
        {isPending ? (
          <div className="space-y-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        ) : isError ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            <CheckRow icon={<DatabaseIcon />} name="PostgreSQL" check={data.checks.database} />
            <CheckRow
              icon={<HardDriveIcon />}
              name="Объектное хранилище"
              check={data.checks.storage}
            />
            <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-xs text-muted-foreground sm:grid-cols-3">
              <div>
                <dt>Версия</dt>
                <dd className="mt-0.5 text-foreground">{data.version}</dd>
              </div>
              <div>
                <dt>Аптайм</dt>
                <dd className="mt-0.5 text-foreground" data-numeric>
                  {data.uptimeSeconds} с
                </dd>
              </div>
              <div>
                <dt>Часовой пояс интерфейса</dt>
                <dd className="mt-0.5 text-foreground">{DISPLAY_TIMEZONE}</dd>
              </div>
            </dl>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RegistryCard() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Реестр типов ProjectItem</CardTitle>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Единый источник, из которого выводятся формы, фильтры, статусы и валидация
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {ITEM_GROUPS.map((group) => (
          <section key={group}>
            <h3 className="mb-2.5 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
              {ITEM_GROUP_LABELS_RU[group]}
            </h3>
            <div className="grid gap-2 lg:grid-cols-2">
              {getItemTypesByGroup(group).map((definition) => (
                <div key={definition.type} className="rounded-md border bg-muted/30 px-3 py-2.5">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-sm font-medium">{definition.labelRu}</span>
                    <code className="text-2xs text-muted-foreground">{definition.type}</code>
                    {definition.detailTable ? (
                      <Badge variant="secondary" className="text-2xs">
                        типизированная таблица
                      </Badge>
                    ) : null}
                    {definition.supportsReview ? (
                      <Badge variant="outline" className="text-2xs">
                        проверка
                      </Badge>
                    ) : null}
                    {definition.supportsNextAction ? (
                      <Badge variant="outline" className="text-2xs">
                        следующее действие
                      </Badge>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {definition.descriptionRu}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {definition.statuses.map((status) => (
                      <StatusBadge key={status.value} label={status.labelRu} tone={status.tone} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </CardContent>
    </Card>
  );
}

export function SystemStatusPage() {
  return (
    <>
      <PageHeader
        title="Состояние системы"
        description="Связь с API, готовность базы данных и объектного хранилища"
      />
      <PageBody className="max-w-5xl space-y-4">
        <HealthCard />
        <RegistryCard />
      </PageBody>
    </>
  );
}
